<template>
    <div
        class="fixed bottom-0 z-[100] lg:z-[50] overflow-hidden transition-all duration-300"
        :style="`left: ${chatBoxPostion.left}px`"
    >
        <ChatBox
            :isChatOpen="isChatOpen"
            :contactGroup="selectedContactGroup"
            @handleCloseChat="handleCloseChat"
            @handleMinimizeChat="handleMinimizeChat"
            ref="chatBox"
        />
    </div>

    <div
        class="fixed bottom-0 transition-all duration-300 z-40 overflow-hidden hidden lg:block"
        v-if="ongoingChats.length"
        :style="`right: ${contactContainerWidth - (hasScroll ? 0 : 30)}px; left: ${hasScroll && isChatOpen ? 380 + 64 : 64 + 8}px`"
    >
        <ChatLabels
            :ongoingChats="ongoingChats"
            :has_scroll="hasScroll"
            :activeContactID="selectedContactGroup.chatId"
            @handleClickChat="handleClickChat"
            @handleCloseChat="handleCloseChat"
        />
    </div>

    <div
        v-if="Object.keys(potentialContacts).length"
        tooltip
        ref="contactContainer"
        class="py-4 px-4 sm:px-0 fixed bottom-20 sm:bottom-0 right-0 overflow-hidden z-50"
    >
        <div
            class="transition-all duration-500 ease-in-out hide-scroll px-2"
            :class="`${isChatBubblesOpen ? 'max-h-screen h-full overflow-y-auto' : 'max-h-0 overflow-hidden'} ${screenSizex.screenSize.value === 'xs' ? '!h-full !max-h-full' : ''}`"
        >
            <ChatBubbles :potentialContacts="potentialContacts" @handleContactClick="handleContactClick" />
        </div>

        <button
            class="bg-purple-500 rounded-full h-12 w-12 items-center justify-center mt-2 mx-auto transition-all duration-300 hidden sm:flex"
            :class="`${isChatBubblesOpen ? 'rotate-180' : 'rotate-0'}`"
            @click="handleBubbleToggle"
        >
            <!-- {{ (typingInRoom, typingInput) }} -->
            <fa v-if="isChatBubblesOpen" :icon="['fal', 'xmark']" class="text-white text-lg" />
            <fa v-else :icon="['fal', 'messages']" class="text-white text-lg" />
        </button>
    </div>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useScreenSize } from "@/composables/globals";
import { useMessenger } from "@/composables/useMessenger";
import { getContactTypeName } from "@/api/AppChatServices";
import { closeConversation, fetchOngoingChats } from "@/api/AppChatServices";
import {
    disconnectSocket,
    establishSocketConn,
    getAgentId,
    getAllRoomMessages,
    getChatsByContactUuid,
} from "@/api/ChatServices";
import { getContactFiles } from "@/api/ContactsServices";
import { getPropertyContacts } from "@/api/PropertiesServices";
import { contactsList } from "@/stores/contactsStore";
import { socketConn } from "@/stores/socketConn";
import { chatroomStore } from "@/stores/messagesStore";
import { ChatBox, ChatBubbles, ChatLabels } from "./chat";

// [info]: ELEM
const contactContainer = ref(null);
const chatBox = ref(null);

// [info]: states
const contactContainerWidth = ref(0);
const ongoingChats = ref([]);
const potentialContacts = ref({});
const selectedContactGroup = ref({});
const isChatOpen = ref(false);
const isChatBubblesOpen = ref(false);
const hasScroll = ref(false);
const pageSize = ref(50);
const pageSkip = ref(0);

const chatBoxPostion = ref({
    // [info]: 64 is the sidebar width + 8 is the padding we need from left
    left: 64 + 8,
});

const route = useRoute();
const screenSizex = useScreenSize();
const messenger = useMessenger();

// [info]: methods
const handleContactClick = async (contactGroup) => {
    const scrollIntoView = () => {
        setTimeout(() => {
            nextTick(() => {
                const chatLabelsContainer = document.getElementById("chatLabelsContainer");
                if (chatLabelsContainer)
                    document.getElementById(contactGroup.chatId)?.scrollIntoView({
                        inline: "start",
                        behavior: "smooth",
                    });
            });
            // [info]: for the opening transition
        }, 300);
    };

    let doesExist = false;
    ongoingChats.value.forEach((contactGroupx) => {
        if (contactGroup.chatId === contactGroupx.chatId) doesExist = true;
    });
    if (!doesExist) ongoingChats.value = [...ongoingChats.value, contactGroup];

    selectedContactGroup.value = contactGroup;
    chatroomStore.chatRoom = contactGroup;
    scrollIntoView();

    checkIfChatHasScroll();
    handleOpenChatBox();
    try {
        await chatBox.value.handleGetConversation(contactGroup);
    } catch (e) {
        console.error(e);
        isChatOpen.value = false;
    }
};

const handleChatBoxPosition = async () => {
    await nextTick(() => {
        const activeChat = document.getElementById(selectedContactGroup.value.chatId);
        if (activeChat && !hasScroll.value) {
            const rects = activeChat.getBoundingClientRect();
            chatBoxPostion.value.left = rects.left;
        } else {
            chatBoxPostion.value.left = 64 + 8;
        }
    });
};

const handleOpenChatBox = async () => {
    if (!selectedContactGroup.value.chatId) return;
    handleChatBoxPosition();
    if (!socketConn.socket?.connected) {
        await establishSocketConn();
    }
    socketConn.socket.emit("join chat", selectedContactGroup.value.chatId);
    setTimeout(() => {
        isChatOpen.value = true;
    }, 300);
};

const handleClickChat = async (contactGroup) => {
    selectedContactGroup.value = contactGroup;
    await checkIfChatHasScroll();
    handleOpenChatBox();
    await chatBox.value.handleGetConversation(contactGroup);
};

const handleCloseChat = async (contactGroup) => {
    let targetIndex = null;
    const handleClose = async () => {
        ongoingChats.value = ongoingChats.value.filter((contactGroupx, index) => {
            if (contactGroupx.chatId === contactGroup.chatId) {
                targetIndex = index;
                return false;
            }

            return true;
        });

        await checkIfChatHasScroll();
    };

    const undoClose = () => ongoingChats.value.splice(targetIndex, 0, contactGroup);

    if (selectedContactGroup.value.chatId === contactGroup.chatId) isChatOpen.value = false;
    await handleClose();
    setTimeout(() => {
        handleChatBoxPosition();
    }, 300); // [info]: for the transition

    try {
        const { data } = await closeConversation(contactGroup);
    } catch (e) {
        undoClose();
        console.error("[ERROR]: at closing conversation", e);
    }
};

const handleMinimizeChat = () => {
    isChatOpen.value = false;
    selectedContactGroup.value = {};
    setTimeout(() => {
        checkIfChatHasScroll();
    }, 300);
};

const checkIfChatHasScroll = async () => {
    const chatLabelsContainer = document.getElementById("chatLabelsContainer");

    if (chatLabelsContainer) {
        await nextTick(() => {
            const hasScrollx = (chatLabelsContainer?.scrollWidth || 0) > (chatLabelsContainer?.clientWidth || 0);
            hasScroll.value = hasScrollx;
            if (hasScrollx) handleChatBoxPosition();
        });
    }
};

const getPotentialContacts = async () => {
    try {
        // const { data } = await fetchRelatedChats();

        switch (route.name) {
            case "contact.board.detail":
                const { data: contactFilesData } = await getContactFiles(route.params.id, route.params.groupId);
                contactsList.relatedContacts = contactFilesData.contacts_group.contacts;
                break;
            case "property.board.detail":
                const { data } = await getPropertyContacts(route.params.propertyId);
                contactsList.relatedContacts = data;
                break;
        }

        const contacts = contactsList.relatedContacts;
        if (!contacts?.length) {
            potentialContacts.value = {};
            return;
        }

        const uniqueTypes = [...new Set(contacts.map((c) => c.contact_type_id))].map((typeId) =>
            getContactTypeName(typeId),
        );
        potentialContacts.value = {};
        uniqueTypes.forEach((typeName) => {
            potentialContacts.value[typeName] = [];
        });

        const contactByUuid = new Map();
        contacts.forEach((c) => {
            contactByUuid.set(c.uuid, {
                ...c,
                typeName: getContactTypeName(c.contact_type_id),
            });
        });

        const allChats = await Promise.allSettled(contacts.map((c) => getChatsByContactUuid(c.uuid)));
        const flatChats = allChats
            .filter((result) => result.status === "fulfilled" && result.value)
            .map((result) => result?.value)
            .flat();
        const privateBuckets = {};
        flatChats
            .filter((chat) => !chat?.isGroupChat)
            .forEach((chat) => {
                const contactUser = chat?.users.find((u) => u.role === "Contact");
                if (!contactUser) return;
                const info = contactByUuid.get(contactUser.uuid);
                if (!info) return;
                const bucket = (privateBuckets[info.typeName] ||= []);
                bucket.push({
                    contact_group_id: info.contact_type_id,
                    chatId: chat._id,
                    contacts: [info],
                });
            });
        console.log("FLatChats", flatChats);
        const groupIdCount = flatChats.reduce((map, chat) => {
            if (chat.isGroupChat) {
                map[chat._id] = (map[chat._id] || 0) + 1;
            }
            return map;
        }, /** @type { Record<string,number> } */ ({}));

        const selectedGroupIds = Object.keys(groupIdCount).filter((id) => groupIdCount[id] >= 2);
        const selectedGroupChats = Array.from(
            new Map(
                flatChats
                    .filter((chat) => chat.isGroupChat && selectedGroupIds.includes(chat._id))
                    .map((chat) => [chat._id, chat]),
            ).values(),
        );

        const newPot = {};
        Object.entries(privateBuckets).forEach(([typeName, arr]) => {
            if (arr.length) {
                newPot[typeName] = arr;
            }
        });
        if (selectedGroupChats.length) {
            newPot["Groups"] = selectedGroupChats.map((chat) => ({
                contact_group_id: 0,
                chatId: chat._id,
                contacts: chat.users.map((user) => {
                    const info = contactByUuid.get(user.uuid);
                    return {
                        // ...info,
                        ...user,
                        badge: info?.badge || "name_icon_q",
                        first_name: info?.first_name || user.firstName,
                        last_name: info?.last_name || user.lastName,
                        initials: messenger.getInitials(user),
                    };
                }),
            }));
        }
        potentialContacts.value = newPot;
        nextTick(() => {
            contactContainerWidth.value = contactContainer.value?.offsetWidth;
        });
    } catch (e) {
        console.error("[ERROR]: at getting potential contacts", e);
    }
};

const handleBubbleToggle = () => {
    isChatBubblesOpen.value = !isChatBubblesOpen.value;
    // getPotentialContacts();
};

watch(
    () => route.name,
    () => {
        isChatBubblesOpen.value = false;
        getPotentialContacts();
    },
);

watch(
    () => contactsList.relatedContacts,
    () => {
        isChatBubblesOpen.value = false;
        if (route.name === 'diary-page'){
            getPotentialContacts();
        }
    },
);

const getOngoingChats = async () => {
    try {
        const { data } = await fetchOngoingChats();
        ongoingChats.value = [...data];
        checkIfChatHasScroll();
    } catch (e) {
    } finally {
    }
};

const messageHandler = {
    async handleNewMessage(newMessageReceived) {
        if (!this.validateMessage(newMessageReceived)) {
            return;
        }

        await this.updateChatMessages(newMessageReceived.chat._id);
        if (!newMessageReceived.chat.bot_id) return;
        await this.handleBotMessage(newMessageReceived);
    },

    validateMessage(message) {
        if (!message || !message.chat || !message.chat._id) {
            console.warn("Invalid message data received:", message);
            return false;
        }
        return true;
    },

    async updateChatMessages(chatId) {
        try {
            await getAllRoomMessages({
                chatRoomId: chatId,
                pageSize: pageSize.value,
                pageSkip: pageSkip.value,
            });
        } catch (error) {
            console.error("Failed to fetch room messages:", error);
        }
    },

    async handleBotMessage(message) {
        if (!message.chat.bot_id || message.sentFrom !== "Widget") {
            return;
        }

        try {
            const { bot_id, _id } = message.chat;
            const agentId = message.chat.users?.find((user) => user.role === "Agent")?._id;

            if (bot_id && _id && agentId && messengerBoxRef.value) {
                await messengerBoxRef.value.sendBotMessage(bot_id, _id, agentId);
            } else {
                console.warn("Missing required data for bot message:", message.chat);
            }
        } catch (error) {
            console.error("Error handling bot message:", error);
        }
    },
};

const messageQueue = {
    queue: [],
    processing: false,

    async add(message) {
        this.queue.push(message);
        if (!this.processing) {
            setTimeout(async () => {
                await this.process();
            }, 0);
        }
    },

    async process() {
        if (this.processing || this.queue.length === 0) {
            return;
        }

        this.processing = true;
        try {
            const batchSize = 10;
            let processed = 0;

            while (this.queue.length > 0 && processed < batchSize) {
                const message = this.queue.shift();
                await messageHandler.handleNewMessage(message);
                processed++;
            }

            if (this.queue.length > 0) {
                setTimeout(async () => {
                    await this.process();
                }, 100);
            }
        } catch (error) {
            console.error("Error processing message queue:", error);
        } finally {
            this.processing = false;
        }
    },
};

onMounted(async () => {
    await getAgentId();
    await getPotentialContacts();
    // isChatBubblesOpen.value = false;
    getOngoingChats();

    if (socketConn.socket == null || !socketConn.socket?.connected) {
        await establishSocketConn();
    }

    socketConn.socket.on("message recieved", async (newMessageReceived) => {
        messageQueue.add(newMessageReceived);
    });
});
</script>
